import FiscalDocTypeCodeEnum from '@/api/Enums/FiscalDocTypeCodeEnum'
import {clone, toFloat, toInt} from '@/lib/helper/helper.js'

export default {
  data() {
    return {}
  },

  props: {
    asc: {
      type   : Boolean,
      default: false
    },
    desc: {
      type   : Boolean,
      default: false
    }
  },

  computed: {
    tableTotals() {
      return this.viewData?.TableTotals || []
    },

    tableTotalsUsed() {
      return this.viewData?.TableTotalsUsed || []
    },

    tableCouponsDiscountTotal() {
      return parseFloat((this.tableCoupons.reduce((value, coupon) => value + coupon.Amount, 0) || 0).toFixed(2))
    },

    tableCouponsUsedDiscountTotal() {
      return parseFloat((this.tableCouponsUsed.reduce((value, coupon) => value + coupon.Amount, 0) || 0).toFixed(2))
    },

    tableCoupons() {
      return this.tableTotals.filter(total => total.Name === 'coupon') || []
    },

    tableCouponsUsed() {
      return this.tableTotalsUsed.filter(total => total.Name === 'coupon') || []
    },

    completedData() {
      const data = JSON.parse(JSON.stringify(this.viewData?.Completed || []))

      if (this.desc) return data.sort((a, b) => parseInt(b.Timestamp) - parseInt(a.Timestamp))

      return data
    }
  },

  methods: {
    cartItemsCanChangeUnitQuantity(cartTimestamp, orderReceipts) {
      const cartOrderReceipts = orderReceipts?.filter(receipt => receipt.InvoiceTypeCodeInternal === FiscalDocTypeCodeEnum.GREEK_DOCUMENT_ORDER && receipt.Timestamp === cartTimestamp) || []
      return cartOrderReceipts.every(receipt => !!receipt.CanceledAt)
    },

    getCartItems(cart, ungrouped = false, singleQuantity = false, filterItemsBy, tab) {
      const items = (cart?.Items || [])
        .filter(item => filterItemsBy !== 'CancelQuantity' ? item.Quantity : true)
        .filter(item => filterItemsBy && item.hasOwnProperty(filterItemsBy) ? item[filterItemsBy] : true)

      if (ungrouped) return items

      const itemsByTableSeat = this.groupCartItemsByTableSeat(items)

      itemsByTableSeat.forEach(tableSeat => {
        tableSeat.Items = this.groupCartItemsByCategory(tableSeat.Items, singleQuantity, filterItemsBy, tab)
      })

      return itemsByTableSeat
    },

    getMenuSelectedOptionsString(menu) {
      let menuArr = menu.Options?.filter(element => element.Selected && element.DisplayType !== 'unit' && element.DisplayType !== 'extra_charges')?.map(element => this.getTranslatedField(element, 'Name'))
      if (menu.Comment) menuArr.push(menu.Comment)
      const menuString = menuArr.join(', ')
      menuArr = null
      return menuString
    },

    groupCartItemsByTableSeat(cartItems) {
      const items = cartItems?.reduce(function (groups, item) {
        const val = item?.Custom?.Seat || 0
        groups[val] = groups[val] || []
        groups[val].push(item)
        return groups
      }, {}) || []

      let prop
      const arr = []
      for (prop in items) {
        if (items.hasOwnProperty(prop)) {
          arr.push({
            Seat : parseInt(prop),
            Name : parseInt(prop) ? `${this.$tc('Restaurant.Tables.Seat.Title', 1)} ${prop}` : this.$tc('Restaurant.Tables.Title', 1),
            Items: items[prop]
          })
        }
      }
      arr.sort((a, b) => {
        return a.Seat - b.Seat
      })

      return arr
    },

    groupCartItemsByCategory(cartItems, singleQuantity = false, filterItemsBy = null, tab) {
      const items = cartItems?.reduce(function (groups, item) {
        const val = item.CategoryId
        groups[val] = groups[val] || []
        groups[val].push(item)
        return groups
      }, {}) || []

      let prop
      const arr = []
      for (prop in items) {
        if (items.hasOwnProperty(prop)) {
          arr.push({
            CategoryId      : parseInt(prop),
            CategoryPriority: parseInt(items[prop][0].CategoryPriority),
            CategoryName    : items[prop][0].CategoryName,
            Items           : items[prop]
          })
        }
      }
      arr.sort((a, b) => {
        return a.CategoryPriority - b.CategoryPriority
      })

      if (singleQuantity) {
        if (!filterItemsBy) {
          arr.forEach(category => {
            const categoryItems = []
            category.Items.forEach(item => {
              for (let i = 0; i < item.Quantity; i++) {
                const itemObj = clone(item)
                itemObj.Quantity = 1
                itemObj.HashId = itemObj.RowId + '-' + i
                categoryItems.push(itemObj)
              }
            })
            category.Items = categoryItems
          })
        } else {
          arr.forEach(category => {
            const categoryItems = []
            category.Items.forEach(item => {
              if (item.UnitId > 1) item.PaymentQuantity = 1

              if (this.menuItemHasCoupon(item) || this.menuItemHasCustomExtraCharges(item) || item.PaymentQuantity === 1) {
                const itemObj = clone(item)
                itemObj.Quantity = item.PaymentQuantity
                itemObj.HashId = itemObj.RowId
                categoryItems.push(itemObj)
              } else {
                for (let i = 0; i < item.PaymentQuantity; i++) {
                  const itemObj = clone(item)
                  const itemCharges = itemObj.Charges || []

                  for (let c = 0; c < itemCharges.length; c++) {
                    itemObj.Charges[c].Quantity = toInt(itemObj.Charges[c].Quantity / item.Quantity)
                    itemObj.Charges[c].Amount = toFloat(itemObj.Charges[c].Amount / item.Quantity)
                  }

                  itemObj.Quantity = itemObj.PaymentQuantity = 1
                  itemObj.HashId = itemObj.RowId + '-' + i
                  categoryItems.push(itemObj)
                }
              }
            })
            category.Items = categoryItems
          })
        }
      }

      return arr
    },

    menuItemPriceTotal(menuItem, calculateCoupons = false, calculateExtraCharges = false) {
      const couponAmount = calculateCoupons ? (this.getMenuItemCoupon(menuItem)?.Amount || 0) : 0
      const extraChargesAmount = calculateExtraCharges ? (this.sumMenuItemExtraCharges(menuItem) || 0) : 0

      let quantity = menuItem.Quantity
      if (this.unpaid || (this.unpaid && this.selectable)) quantity = menuItem.PaymentQuantity
      if (this.canceled || (this.canceled && this.selectable)) quantity = menuItem.CancelQuantity
      if (menuItem.UnitId > 1) quantity = 1

      const menuItemPrice = (parseFloat(menuItem.Price) * quantity) - couponAmount + extraChargesAmount

      return menuItemPrice
    },

    sumMenuItemExtraCharges(menuItem, chargesField = 'Charges', priceField = 'Amount', quantityField = 'Quantity') {
      const charges = menuItem && menuItem[chargesField] || []
      //const total = charges.reduce((totalAmount, charge) => totalAmount + toFloat(parseFloat(charge[priceField]) * parseInt(charge[quantityField])), 0) || 0
      const total = charges.reduce((totalAmount, charge) => totalAmount + toFloat(parseFloat(charge[priceField])), 0) || 0

      return toFloat(total)
    },

    cartHasItemsWithCoupon(cart) {
      const items = this.getOrderMenuItems(cart)
      return items?.some(item => !!item?.Coupon) ?? false
    },

    cartTotals(cart) {
      return cart?.Totals || []
    },

    cartExtraCharges(cart) {
      const totals = this.cartTotals(cart) || []
      return totals.filter(total => total.Name === 'extra_charge') || []
    },

    cartHasExtraCharges(cart) {
      return !!this.cartExtraCharges(cart)?.length
    },

    cartItemExtraCharges(cart, item) {
      return this.cartExtraCharges(cart).filter(c => c.RowId === item?.RowId)
    },
    cartItemHasExtraCharges(cart, item) {
      return this.cartItemExtraCharges(cart, item).length > 0
    },

    cartCoupons(cart) {
      const totals = this.cartTotals(cart) || []
      return totals.filter(total => total.Name === 'coupon') || []
    },

    cartHasCoupons(cart) {
      return !!this.cartCoupons(cart)?.length
    },

    cartOrderPriceTotal(cart, calculateCoupons = false, calculateExtraCharges = true) {
      const items = this.getOrderMenuItems(cart)
      const priceTotal = items?.reduce((value, menuItem) => value + this.menuItemPriceTotal(menuItem, calculateCoupons), 0) || 0
      let totalDiscount = 0
      let totalCharges = 0

      if (calculateCoupons) {
        const totals = this.cartCoupons(cart)
        totalDiscount = calculateCoupons ? totals.reduce((value, coupon) => value + coupon.Amount, 0) || 0 : 0
      }

      if (calculateExtraCharges) {
        const totals = this.cartExtraCharges(cart)
        totalCharges = calculateExtraCharges ? totals.reduce((value, coupon) => value + coupon.Amount, 0) || 0 : 0
      }

      return priceTotal - totalDiscount + totalCharges
    },

    canceledPriceTotal() {
      let total = 0
      this.completedData.forEach(cart => {
        total = total + this.cartOrderPriceTotal(cart, true)
      })

      return parseFloat(total.toFixed(2))
    },

    cartsPriceTotal(calculateTableCoupons = false) {
      let total = 0
      this.completedData.forEach(cart => {
        total = total + this.cartOrderPriceTotal(cart, true)
      })

      total -= calculateTableCoupons ? this.tableCouponsDiscountTotal : 0

      return parseFloat(total.toFixed(2))
    },

    menuItemHasCoupon(item) {
      return !!this.getMenuItemCoupon(item)
    },

    getMenuItemCoupon(item) {
      return item?.Coupon || null
    },

    menuItemHasExtraCharges(item) {
      return this.getMenuItemExtraCharges(item)?.length > 0
    },

    menuItemHasCustomExtraCharges(item) {
      return this.getMenuItemExtraCharges(item)?.some(charge => charge.IsCustom)
    },

    getMenuItemExtraCharges(item) {
      return item?.Charges || []
    }

  }
}
